import { callIfConnectedAndDefined } from "../utilities/components.js";
import { DickTokComponent } from "./Component.js";
import { defineElement } from "./registry.js";

/**
 * @typedef {import('./Ad.js').DickTokAd} DickTokAd
 * @typedef {import('./Feed.js').DickTokFeed} DickTokFeed
 * @typedef {import('./Post.js').DickTokPost} DickTokPost
 */

/**
 * @typedef {object} ChildElement
 * @extends {DickTokComponent}
 * @property {(() => void)|undefined} onViewportFocus
 * @property {(() => void)|undefined} onViewportBlur
 * @property {(() => void)|undefined} onNearViewport
 */

export class DickTokFeedItem extends DickTokComponent {
  connectedCallback() {
    super.connectedCallback();
    this.setupIntersectionObservers();
  }

  /** Observe when the element is within the parent feed element viewport. */
  setupIntersectionObservers() {
    /** @type {HTMLElement} Parent feed element, if child of a feed */
    const feedElement = this.closest("dicktok-feed");
    if (!feedElement) {
      return;
    }

    const childElement = /** @type {ChildElement} */ (this.firstElementChild);

    // Call callbacks on FeedItem child when it gains or loses focus
    const focusObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= 0.5) {
          callIfConnectedAndDefined(childElement, "onViewportFocus");
        } else {
          callIfConnectedAndDefined(childElement, "onViewportBlur");
        }
      },
      {
        root: feedElement,
        rootMargin: "0px",
        // These are the ratios at which to call the callback.
        // Call the callback when visible at all (0) and when the element
        // covers half of the screen (0.5)
        threshold: [0, 0.5],
      },
    );
    focusObserver.observe(this);

    // Call callbacks on FeedItem child when it is near the viewport
    const proximityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Initially all the posts intersect because they have no height until
          // the viewport height is initialized by the Feed component. Don't call
          // `onNearViewport` until the height is initialized.
          if (feedElement.clientHeight > 0 && entry.isIntersecting) {
            callIfConnectedAndDefined(childElement, "onNearViewport");
          }
        }
      },
      {
        root: feedElement,
        rootMargin: "150% 0px",
        // Call the callback when overlapping the expanded margin at all.
        threshold: [0],
      },
    );
    proximityObserver.observe(this);

    this.disconnectSignal.addEventListener("abort", () => {
      focusObserver.disconnect();
      proximityObserver.disconnect();
    });
  }
}

defineElement("dicktok-feeditem", DickTokFeedItem);
